import { useState, useEffect } from 'react';
import { X, Camera, Loader2, Gift, Repeat, Info, ChevronDown, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import { uploadImage } from '../services/imageUpload';
import * as analytics from '../services/analytics';
import { GIVEAWAY_PRESETS as PRESET_IMAGES } from '../assets';

const CATEGORIES = [
  'Power Tools', 'Hand Tools', 'Garden Tools', 'Ladders & Scaffolding',
  'Painting Supplies', 'Plumbing', 'Electrical',
  'Camping & Outdoor', 'Sports Equipment', 'Bikes & Scooters',
  'Yard Games', 'Fishing & Hunting',
  'Kitchen Appliances', 'Small Appliances', 'Cookware & Bakeware', 'Party Supplies',
  'Electronics', 'Cameras & AV', 'Gaming',
  'Moving Equipment', 'Storage & Organization',
  'Baby & Kids Gear', 'Toys & Games', 'Books & Media',
  'Holiday Decorations', 'Seasonal Items',
  'Cleaning Equipment', 'Safety Equipment', 'Musical Instruments',
  'Furniture', 'Clothing & Accessories', 'Pet Supplies', 'Other',
];

const AVAILABILITIES = [
  ['public', 'Public'],
  ['friends', 'Friends Only'],
  ['community', 'Community Members'],
  ['unavailable', 'Unavailable'],
  ['invisible', 'Hidden from all'],
];

// Availability options that don't make sense for a giveaway (one-time free hand-off).
const GIVEAWAY_DISABLED_AVAILABILITIES = new Set(['unavailable', 'invisible']);

const ITEM_TYPES = [
  { value: 'lend', label: 'Lend', icon: Repeat, description: 'Others can borrow and return it' },
  { value: 'giveaway', label: 'Giveaway', icon: Gift, description: 'Give it away for free (expires in 2 weeks)' },
];

const inp = "w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:border-[#004B00] focus:ring-2 focus:ring-[#004B00]/20 outline-none font-nunito text-sm bg-white transition-all";
const lbl = "block text-sm font-semibold text-gray-700 mb-1.5 font-nunito";

const BLANK = {
  title: '', description: '', category: 'Power Tools',
  user_guide: '', photo: '', photos: [], availability: 'public',
  community_ids: [], item_type: 'lend',
};

export default function ItemFormModal({ onClose, onSuccess, defaultItemType = 'lend' }) {
  const [loading, setLoading] = useState(false);
  const [communities, setCommunities] = useState([]);
  const [form, setForm] = useState({ ...BLANK, item_type: defaultItemType });

  useEffect(() => {
    api.get('/communities').then(r => setCommunities(r.data)).catch(() => {});
  }, []);

  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    const current = (form.photos || []).filter(Boolean);
    if (current.length >= 4) {
      toast.error('Maximum 4 photos allowed');
      e.target.value = '';
      return;
    }
    try {
      const { url } = await uploadImage(file);
      setForm(f => {
        const all = (f.photos || []).filter(Boolean);
        if (all.length >= 4) return f;
        const updated = [...all, url];
        return { ...f, photo: updated[0], photos: updated };
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Photo upload failed');
    } finally {
      e.target.value = '';
    }
  };

  const removePhoto = (idx) => {
    setForm(f => {
      const updated = (f.photos || []).filter((_, i) => i !== idx);
      return { ...f, photos: updated, photo: updated[0] || '' };
    });
  };

  const togglePreset = (url) => {
    setForm(f => {
      const photos = f.photos || [];
      if (photos.includes(url)) {
        const updated = photos.filter(p => p !== url);
        return { ...f, photos: updated, photo: updated[0] || '' };
      }
      if (photos.length >= 4) { toast.error('Maximum 4 photos allowed'); return f; }
      const updated = [...photos, url];
      return { ...f, photos: updated, photo: updated[0] || '' };
    });
  };

  const toggleCommunity = (id) => setForm(f => ({
    ...f,
    community_ids: f.community_ids.includes(id)
      ? f.community_ids.filter(c => c !== id)
      : [...f.community_ids, id],
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.category) { toast.error('Title and category are required'); return; }
    setLoading(true);
    try {
      const res = await api.post('/items', form);
      toast.success('Item added to your library!');
      analytics.track(form.item_type === 'giveaway' ? 'giveaway_created' : 'item_created', { category: form.category, availability: form.availability });
      onSuccess?.(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]" data-testid="item-form-modal">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-stone-100 flex-shrink-0">
          <h3 className="font-poppins font-semibold text-gray-900 text-lg">Add Item to Library</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={16} className="text-gray-400" />
          </button>
        </div>

        {/* Scrollable form body */}
        <div className="overflow-y-auto flex-1 px-5 py-5">
          <form id="item-form" onSubmit={handleSubmit} className="space-y-5">

            {/* Photos */}
            <div>
              <label className={lbl}>Photos <span className="text-xs text-gray-400 font-normal">(up to 4)</span></label>
              <div className="grid grid-cols-4 gap-2">
                {[0,1,2,3].map(idx => {
                  const src = (form.photos || [])[idx];
                  return (
                    <div key={idx} className="relative aspect-square rounded-xl overflow-hidden border-2 border-dashed border-stone-200 bg-stone-50 flex items-center justify-center group">
                      {src ? (
                        <>
                          <img src={src} alt={`photo ${idx+1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removePhoto(idx)}
                            className="absolute top-1 right-1 bg-white/90 text-red-500 rounded-full p-0.5 shadow opacity-0 group-hover:opacity-100 transition-opacity z-10"
                          >
                            <X size={11} />
                          </button>
                          {idx === 0 && (
                            <span className="absolute bottom-1 left-1 bg-[#004B00] text-white text-xs px-1 py-0.5 rounded font-nunito text-[10px]">Main</span>
                          )}
                        </>
                      ) : (form.photos || []).length <= idx ? (
                        <label className="absolute inset-0 cursor-pointer flex flex-col items-center justify-center gap-1 text-stone-300 hover:text-stone-400 transition-colors">
                          <Plus size={16} />
                          <span className="text-xs font-nunito">Add</span>
                          <input type="file" accept="image/*" onChange={handlePhoto} className="hidden" data-testid={`modal-photo-input-${idx}`} />
                        </label>
                      ) : (
                        <Camera size={16} className="text-stone-300" />
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 font-nunito mt-1">Max 5 MB each · First photo is the main image</p>

              {/* Preset quick-pick images */}
              <div className="mt-3">
                <p className="text-xs text-gray-500 font-nunito font-semibold mb-1.5">Or pick a preset illustration:</p>
                <div className="flex gap-2 flex-wrap">
                  {PRESET_IMAGES.map((preset) => {
                    const selected = (form.photos || []).includes(preset.url);
                    return (
                      <button
                        key={preset.url}
                        type="button"
                        onClick={() => togglePreset(preset.url)}
                        title={preset.label}
                        data-testid={`preset-${preset.label.toLowerCase().replace(/\s/g, '-')}`}
                        className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${
                          selected ? 'border-[#004B00] shadow-md ring-1 ring-[#004B00]/30' : 'border-stone-200 opacity-70 hover:opacity-100 hover:border-stone-400'
                        }`}
                      >
                        <img src={preset.url} alt={preset.label} className="w-full h-full object-cover" />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Title */}
            <div>
              <label className={lbl}>Item Name *</label>
              <input data-testid="item-title" required className={inp} value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g., DeWalt Cordless Drill" />
            </div>

            {/* Description */}
            <div>
              <label className={lbl}>Description</label>
              <textarea data-testid="item-description" rows={3} className={`${inp} resize-none`}
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Describe the item, what it can do, any important notes..." />
            </div>

            {/* Category */}
            <div>
              <label className={lbl}>Category *</label>
              <div className="relative">
                <select data-testid="item-category" className={`${inp} appearance-none pr-10`} value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
              </div>
            </div>

            {/* Listing Type */}
            <div>
              <label className={lbl}>Listing Type *</label>
              <div className="grid grid-cols-2 gap-3">
                {ITEM_TYPES.map(({ value, label, icon: Icon, description }) => (
                  <label key={value}
                    className={`cursor-pointer p-4 rounded-xl border-2 transition-all ${form.item_type === value ? 'border-[#004B00] bg-[#004B00]/5' : 'border-stone-200 hover:border-stone-300'}`}>
                    <input type="radio" name="item_type" value={value}
                      checked={form.item_type === value}
                      onChange={e => setForm(f => {
                        const nextType = e.target.value;
                        // If switching TO giveaway and current availability isn't allowed, snap back to 'public'.
                        const nextAvailability = nextType === 'giveaway' && GIVEAWAY_DISABLED_AVAILABILITIES.has(f.availability)
                          ? 'public'
                          : f.availability;
                        return { ...f, item_type: nextType, availability: nextAvailability };
                      })}
                      className="hidden" data-testid={`item-type-${value}`} />
                    <div className="flex items-center gap-2 mb-1">
                      <Icon size={16} className={form.item_type === value ? 'text-[#004B00]' : 'text-gray-500'} />
                      <span className={`font-nunito font-semibold text-sm ${form.item_type === value ? 'text-[#004B00]' : 'text-gray-700'}`}>{label}</span>
                    </div>
                    <p className="text-xs text-gray-500 font-nunito">{description}</p>
                  </label>
                ))}
              </div>
              {form.item_type === 'giveaway' && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <Info size={15} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-700 font-nunito">
                    Giveaways expire after 2 weeks but can be extended once. When someone claims your giveaway, you'll earn 5 Neighborli Progress points!
                  </p>
                </div>
              )}
            </div>

            {/* User Guide */}
            <div>
              <label className={lbl}>User Guide / Instructions</label>
              <textarea rows={2} className={`${inp} resize-none`} value={form.user_guide}
                onChange={e => setForm(f => ({ ...f, user_guide: e.target.value }))}
                placeholder="Any special instructions, safety tips, how to use..." />
            </div>

            {/* Availability */}
            <div>
              <label className={lbl}>Availability</label>
              <div className="space-y-1">
                {AVAILABILITIES.map(([val, label]) => {
                  const isDisabled = form.item_type === 'giveaway' && GIVEAWAY_DISABLED_AVAILABILITIES.has(val);
                  return (
                    <label
                      key={val}
                      title={isDisabled ? 'Not available for giveaways' : undefined}
                      data-testid={`availability-option-${val}`}
                      className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                        isDisabled
                          ? 'cursor-not-allowed opacity-50'
                          : 'cursor-pointer hover:bg-stone-50'
                      }`}
                    >
                      <input type="radio" name="availability" value={val}
                        checked={form.availability === val}
                        disabled={isDisabled}
                        onChange={e => setForm(f => ({ ...f, availability: e.target.value }))}
                        className="text-[#004B00] accent-[#004B00] disabled:cursor-not-allowed" />
                      <span className={`font-nunito text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>{label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Community selection */}
            {form.availability === 'community' && communities.length > 0 && (
              <div>
                <label className={lbl}>Select Communities</label>
                <div className="space-y-2">
                  {communities.map(c => (
                    <label key={c.id} className="flex items-center gap-3 cursor-pointer p-2.5 rounded-lg hover:bg-stone-50 transition-colors border border-stone-100">
                      <input type="checkbox" checked={form.community_ids.includes(c.id)}
                        onChange={() => toggleCommunity(c.id)} className="accent-[#004B00]" />
                      <span className="font-nunito text-sm text-gray-700">{c.name}</span>
                      <span className="text-xs text-gray-400 ml-auto">{c.member_count} members</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 border-t border-stone-100 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 border-2 border-stone-200 text-gray-700 py-2.5 rounded-xl font-nunito font-semibold hover:bg-gray-50 transition-all">
            Cancel
          </button>
          <button type="submit" form="item-form" disabled={loading} data-testid="save-item-btn"
            className="flex-1 bg-[#004B00] hover:bg-[#003800] text-white py-2.5 rounded-xl font-nunito font-semibold transition-all disabled:opacity-60 flex items-center justify-center gap-2">
            {loading && <Loader2 size={15} className="animate-spin" />}
            {loading ? 'Saving…' : 'Add to Library'}
          </button>
        </div>
      </div>
    </div>
  );
}
